import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Blurhash } from 'react-blurhash';
import './profilepage.css';
import { useNavigate } from 'react-router-dom';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import cindy from '../assets/profilepage/cindy.jpeg';
import { PROFILE_BLURB, PROJECTS } from './constants';

export const ProfilePage: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemIndex, setItemIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const navigate = useNavigate();

  const currentProject = PROJECTS[currentIndex];

  useEffect(() => {
    document.title = 'CT Consultants';
    const favicon = document.querySelector("link[rel='icon']") as HTMLLinkElement;
    const prevHref = favicon?.href;
    if (favicon) favicon.href = '/img11.jpg';
    return () => {
      document.title = 'Javan Mynah';
      if (favicon) favicon.href = prevHref;
    };
  }, []);

  useEffect(() => {
    setImageLoaded(false);
    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.src = currentProject.portfolioItems[itemIndex].image;

    const num_portfolio = currentProject.portfolioItems.length;
    const nextImage = new Image();
    const prevImage = new Image();
    nextImage.src = currentProject.portfolioItems[(itemIndex + 1) % num_portfolio].image;
    prevImage.src = currentProject.portfolioItems[((itemIndex - 1) + num_portfolio) % num_portfolio].image;

    return () => { img.onload = null; };
  }, [itemIndex, currentIndex]);

  const handleItemPrev = () => {
    setItemIndex((prev) => (prev === 0 ? currentProject.portfolioItems.length - 1 : prev - 1));
  };

  const handleItemNext = () => {
    setItemIndex((prev) => (prev === currentProject.portfolioItems.length - 1 ? 0 : prev + 1));
  };

  const currentItem = currentProject.portfolioItems[itemIndex];

  return (
    <div className="profile-page">
      {/* Header */}
      <header className="profile-header">
        <div className="profile-header-content">
          <div className="profile-logo">
            <img src={process.env.PUBLIC_URL + '/logo.svg'} alt="CT Consultants logo" className="profile-logo-img" />
            <h1>CT Consultants</h1>
          </div>
				<button className="contact-btn" onClick={() => { navigate("/sample-profile/contact-us"); }}>
					Contact
				</button>
        </div>
      </header>

      {/* Main Content */}
      <Container maxWidth={false} sx={{ py: '2vmin', px: '4vmin' }}>
        <Grid container spacing={3} alignItems="flex-start">

          {/* Left Column - About */}
          <Grid size={{ xs: 12, md: 3 }}>
            <section className="profile-about">
              <div className="about-content">
                <div className="profile-photo-container">
                  <img
                    src={cindy}
                    alt="Portrait"
                    className="profile-photo"
                  />
                </div>
                <div className="about-text">
                  {PROFILE_BLURB.split('\n').map(
                    (text: string, i: number) => <p key={i} className="about-paragraph">{text}</p>
                  )}
                </div>
              </div>
            </section>
          </Grid>

          {/* Center Column - Portfolio */}
          <Grid size={{ xs: 12, md: 7 }}>
            <section className="profile-portfolio">
              <div className="portfolio-main">
                <div className="portfolio-project-title">{currentProject.title}</div>
                <div
                  className="portfolio-image-container"
                  onClick={() => setLightboxOpen(true)}
                >
                  {!imageLoaded && (
                    <Blurhash
                        hash={currentItem.blurhash}
                        width="100%"
                        height="100%"
                        resolutionX={32}
                        resolutionY={32}
                        punch={1}
                      />
                    )}

                    <img
                      src={currentItem.image}
                      alt={currentItem.caption}
                      className="portfolio-image"
                      style={{
                        display: imageLoaded ? "block" : "none",
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  <div className="portfolio-caption">{currentItem.caption}</div>

                  {/* Navigation */}
                  <div className="portfolio-nav">
                    <button
                      className="nav-btn nav-prev"
                      onClick={(e) => { e.stopPropagation(); handleItemPrev(); }}
                      aria-label="Previous"
                    >
                      ‹
                    </button>
                    <button
                      className="nav-btn nav-next"
                      onClick={(e) => { e.stopPropagation(); handleItemNext(); }}
                      aria-label="Next"
                    >
                      ›
                    </button>
                  </div>

                  {/* Counter */}
                  <div className="portfolio-counter">
                    {itemIndex + 1}/{currentProject.portfolioItems.length}
                  </div>
                </div>

                <div className="portfolio-description">{currentProject.description}</div>
              </div>
            </section>
          </Grid>

          {/* Right Column - Project Selector */}
          <Grid size={{ xs: 12, md: 2 }}>
            <section className="profile-timeline">
              <div className="timeline-container">
                {PROJECTS.map((entry, idx) => (
                  <div
                    key={idx}
                    className={`timeline-item ${idx === currentIndex ? 'active' : ''}`}
                    onClick={() => { setCurrentIndex(idx); setItemIndex(0); }}
                  >
                    <div className="timeline-dot"></div>
                    <div className="timeline-year">{entry.title}</div>
                  </div>
                ))}
              </div>
            </section>
          </Grid>

        </Grid>
      </Container>
      {/* Lightbox */}
      {lightboxOpen && createPortal(
        <div className="lightbox-overlay" onClick={() => setLightboxOpen(false)}>
          <button className="lightbox-close" onClick={() => setLightboxOpen(false)} aria-label="Close">
            ×
          </button>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={currentItem.image} alt={currentItem.caption} className="lightbox-image" />
            <div className="lightbox-caption">{currentItem.caption}</div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
